import {
  type DRSConfig,
  DRSInfo,
  DRSProviderType,
  DRSStatus,
} from "@/governance/types/drs"
import { ChainId } from "@/web3/constants/chains"

enum Environment {
  Prod = "https://api.tally.xyz",
  Staging = "https://api.staging.tally.xyz",
}

// key is the org ID
const DRS_CONFIG: Record<Environment, Record<string, DRSConfig>> = {
  [Environment.Staging]: {},
  [Environment.Prod]: {
    // Rari Chain Mainnet rari-dao
    "2530797033149695623": {
      name: "rarifoundation",
      chainId: ChainId.RariChainMainnet,
      defaultThreshold: 15,
      provider: DRSProviderType.KARMA,
      delegatesEligibilityOracleAddress:
        "0xAc5315251B91D2Fa4bf61cE479DF792778A7f464",
      profileBaseUrl: "https://rari.karmahq.xyz/profile",
      docs: {
        drsCalculationLink:
          "https://forum.rari.foundation/t/karma-oracle-for-staking-rewards/2364",
      },
      displayStatusBadge: true,
    },
    // Arbitrum Mainnet
    "2206072050315953936": {
      name: "arbitrum",
      chainId: ChainId.ArbitrumOne,
      defaultThreshold: 15,
      provider: DRSProviderType.KARMA,
      profileBaseUrl: "https://arbitrum.karmahq.xyz/profile",
      docs: {
        drsCalculationLink: "",
      },
      displayStatusBadge: false,
    },
    // Obol Mainnet
    "2413388957975839812": {
      chainId: ChainId.EthereumMainnet,
      name: "obol",
      defaultThreshold: 65,
      provider: DRSProviderType.CURIA,
      profileBaseUrl: "https://obol.curiahub.xyz/delegate",
      showInfoCard: true,
      infoCardDisplayName: "Obol",
      docs: {
        drsCalculationLink:
          "https://docs.obol.org/community-and-governance/governance/delegate-guide/delegate-reputation-score-and-compensation",
        forumLinkAccountUrl:
          "https://community.obol.org/t/public-forum-to-wallet-link-verification/559",
        autoDelegateInfoLink:
          "https://docs.obol.org/community-and-governance/obol-token/staking-and-stobol",
      },
      displayStatusBadge: true,
      getDRSStatusTooltip: (drsInfo: DRSInfo, threshold: number): string => {
        const isEstablished = !drsInfo.isProbation
        const isActive = drsInfo.status === DRSStatus.ACTIVE
        const isGhost = drsInfo.status === DRSStatus.GHOST

        if (isGhost) {
          return `Ghost delegates have voting power delegated but have not participated in any governance votes. Delegates need a minimum DRS (Delegate Reputation Score) of ${threshold} to be active.`
        }

        if (isActive && isEstablished) {
          return `Active delegates have a DRS (Delegate Reputation Score) of ${threshold} or higher based on voting participation, voting impact, timeliness, rationales, and forum engagement.`
        }

        if (isActive && !isEstablished) {
          return `This delegate is new with less than 5 proposals since their first vote. They currently meet the Active threshold of ${threshold} DRS.`
        }

        if (!isActive && isEstablished) {
          return `Inactive delegates have a DRS below ${threshold}. The score reflects voting participation, voting impact, timeliness, rationales, and forum engagement.`
        }

        if (!isActive && !isEstablished) {
          return `This delegate is new with less than 5 proposals since their first vote. Their current DRS is below the Active threshold of ${threshold}.`
        }

        return `Delegates need a minimum DRS (Delegate Reputation Score) of ${threshold} to be active.`
      },
    },
    // Compound Mainnet
    "2206072050458560433": {
      name: "compound",
      chainId: ChainId.EthereumMainnet,
      // defaultThreshold defines the minimum Delegate Reputation Score (DRS) required for a delegate to be considered active.
      defaultThreshold: 1,
      provider: DRSProviderType.KARMA,
      profileBaseUrl: "https://compound.karmahq.xyz/profile",
      docs: {
        drsCalculationLink: "",
      },
      displayStatusBadge: false,
    },
  },
}

// Cache Configuration
export const DRS_CACHE_CONFIG = {
  STALE_TIME_MS: 1000 * 60 * 60, // 1 hour
  CACHE_TIME_MS: 1000 * 60 * 60 * 24, // 24 hours
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY_MS: 1000,
} as const

// Query Keys
export const DRS_QUERY_KEYS = {
  USER: "drs-score",
  BULK: "drs-bulk-score",
} as const

export const DRS_R2_BUCKET_URL =
  "https://pub-26963d35241d454698ff9a43113092fa.r2.dev"

// LST-constants safe getter without throwing an error
export function getDRSConstants(orgId?: string) {
  if (!orgId) {
    return null
  }

  const envUrl = process.env.NEXT_PUBLIC_TALLY_API_URL
  const env = Object.values(Environment).find((e) => e === envUrl)

  if (!env) {
    console.warn(`Unknown environment URL: ${envUrl}`)
    return null
  }

  return DRS_CONFIG[env]?.[orgId] ?? null
}
